import type { AdapterApplyAnswers, AdapterBinding, AdapterQuestion } from "./types";
import { resolveAdapterPathSelectors } from "./pathSelectors";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getAtPath = (value: Record<string, unknown>, path: string): unknown =>
  path.split(".").reduce<unknown>((current, segment) => {
    const match = segment.match(/^([^\[\]]+)\[(\d+)\]$/);
    if (match && isRecord(current)) {
      const collection = current[match[1]];
      if (Array.isArray(collection)) return collection[Number(match[2])];
    }
    return isRecord(current) ? current[segment] : undefined;
  }, value);

const setAtPath = (value: Record<string, unknown>, path: string, nextValue: unknown): void => {
  const segments = path.split(".");
  let current: Record<string, unknown> = value;
  for (const [index, segment] of segments.entries()) {
    const last = index === segments.length - 1;
    const match = segment.match(/^([^\[\]]+)\[(\d+)\]$/);
    if (!match) {
      if (last) current[segment] = nextValue;
      else {
        if (!isRecord(current[segment])) current[segment] = {};
        current = current[segment] as Record<string, unknown>;
      }
      continue;
    }
    const [, key, rawIndex] = match;
    if (!Array.isArray(current[key])) current[key] = [];
    const collection = current[key] as unknown[];
    const itemIndex = Number(rawIndex);
    if (last) collection[itemIndex] = nextValue;
    else {
      if (!isRecord(collection[itemIndex])) collection[itemIndex] = {};
      current = collection[itemIndex] as Record<string, unknown>;
    }
  }
};

const getAnswer = (answers: AdapterApplyAnswers, path: string | undefined): unknown => {
  if (!path) return undefined;
  const resolvedPath = resolveAdapterPathSelectors(answers, path);
  return resolvedPath ? getAtPath(answers, resolvedPath) : undefined;
};

const isPresent = (value: unknown): boolean =>
  value !== undefined && value !== null && !(typeof value === "string" && value.trim() === "");

const renderBindingTemplate = (
  template: string,
  value: unknown,
  answers: AdapterApplyAnswers,
): string | undefined => {
  let unresolved = false;
  const rendered = template
    .replaceAll("{{value}}", isPresent(value) ? String(value) : "")
    .replaceAll(/{{answers\.([^}]+)}}/g, (_, path: string) => {
      const answer = getAnswer(answers, path);
      if (!isPresent(answer)) {
        unresolved = true;
        return "";
      }
      return String(answer);
    });
  return unresolved || (template.includes("{{value}}") && !isPresent(value)) ? undefined : rendered;
};

/** Applies declarative adapter-state bindings to a resource payload. */
export const applyAdapterBindings = (
  payload: Record<string, unknown>,
  resource: { bindings?: AdapterBinding[] },
  answers: AdapterApplyAnswers,
): Record<string, unknown> => {
  const next = structuredClone(payload);
  for (const binding of resource.bindings ?? []) {
    const targetPath = resolveAdapterPathSelectors(next, binding.targetPath);
    if (!targetPath) continue;
    const sourceValue = getAnswer(answers, binding.sourcePath);
    const value = binding.valueTemplate
      ? renderBindingTemplate(binding.valueTemplate, sourceValue, answers)
      : sourceValue;
    if (isPresent(value)) setAtPath(next, targetPath, value);
  }
  return next;
};

/** What a binding fills in from the answers: the value, or the question it's still waiting on. */
export type AdapterBoundValue = { value: unknown } | { waitingOn: Pick<AdapterQuestion, "label" | "type"> };

/**
 * What the resource's bindings would fill in from the current answers, by binding target path (such as
 * `columns[id=x].entity`). The adapter only fills these in when it creates the resource, so this says what to set by
 * hand on one that already exists.
 */
export const getAdapterBoundValues = (
  resource: { bindings?: AdapterBinding[] },
  answers: AdapterApplyAnswers,
  questions: AdapterQuestion[],
): Record<string, AdapterBoundValue> => {
  const bound: Record<string, AdapterBoundValue> = {};
  for (const binding of resource.bindings ?? []) {
    const sourceValue = getAnswer(answers, binding.sourcePath);
    const value = binding.valueTemplate
      ? renderBindingTemplate(binding.valueTemplate, sourceValue, answers)
      : sourceValue;
    if (isPresent(value)) {
      bound[binding.targetPath] = { value };
      continue;
    }
    const templatePaths = Array.from(binding.valueTemplate?.matchAll(/{{answers\.([^}]+)}}/g) ?? [], (match) => match[1]);
    const missing = [binding.sourcePath, ...templatePaths].find((path) => path && !isPresent(getAnswer(answers, path)));
    const question = questions.find((candidate) => candidate.key === missing);
    if (question) bound[binding.targetPath] = { waitingOn: { label: question.label, type: question.type } };
  }
  return bound;
};
